// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-82 — "Emails & copies": per document type, who gets a copy of the signed document
// (DocuSign CC), the wording of the signature email, and who is told once it is signed.
//
// Nothing here is required. A document type left untouched sends exactly the envelope it always
// sent. Only Owner and Admin can save (carerx.esign.configure); everyone else sees the settings read-only.

import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import type { EsignDocType, EsignEmailSettings } from '../../api/esignSettings'
import { friendlyError } from '../../api/errors'
import { useEsignSettings, useSaveEsignSettings } from '../../hooks/queries/esignSettings'
import {
  DOC_TYPE_LABELS, addressesToText, describeSettingsError, isOffDomain, looksLikeAddress, parseAddresses, unknownPlaceholders,
} from '../../lib/esignSettings'

interface Draft { ccText: string; copyText: string; row: EsignEmailSettings }
const toDraft = (row: EsignEmailSettings): Draft =>
  ({ row, ccText: addressesToText(row.cc_emails), copyText: addressesToText(row.copy_emails) })

export default function EsignEmailSettingsCard() {
  const q = useEsignSettings()
  const save = useSaveEsignSettings()
  const [tab, setTab] = useState<EsignDocType>('enrollment')
  const [drafts, setDrafts] = useState<Partial<Record<EsignDocType, Draft>>>({})
  const [saved, setSaved] = useState<EsignDocType | null>(null)

  const data = q.data
  const stored = data?.settings.find(s => s.doc_type === tab)
  useEffect(() => { setSaved(null); save.reset() }, [tab])   // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return null
  if (q.error || !data || !stored)
    return <div style={box} role="alert">E-sign email settings could not be loaded: {friendlyError(q.error)}</div>

  const draft = drafts[tab] ?? toDraft(stored)
  const readOnly = !data.can_configure
  const cc = parseAddresses(draft.ccText)
  const copy = parseAddresses(draft.copyText)
  const offDomain = cc.filter(e => looksLikeAddress(e) && isOffDomain(e, data.pharmacy_domains))
  const next: EsignEmailSettings = {
    ...draft.row, cc_emails: cc, copy_emails: copy,
    cc_offdomain_ack: draft.row.cc_offdomain_ack.filter(e => offDomain.includes(e)),
  }
  const dirty = JSON.stringify(normalise(next)) !== JSON.stringify(normalise(stored))

  const localErrors = [
    ...cc.filter(e => !looksLikeAddress(e)).map(e => `cc_emails_invalid:${e}`),
    ...copy.filter(e => !looksLikeAddress(e)).map(e => `copy_emails_invalid:${e}`),
    ...(cc.length > data.limits.addresses ? ['cc_emails_too_many'] : []),
    ...(copy.length > data.limits.addresses ? ['copy_emails_too_many'] : []),
    ...(next.cc_enabled && cc.length === 0 ? ['cc_emails_enabled_without_addresses'] : []),
    ...(next.copy_enabled && copy.length === 0 ? ['copy_emails_enabled_without_addresses'] : []),
    ...(next.cc_enabled ? offDomain.filter(e => !next.cc_offdomain_ack.includes(e)).map(e => `cc_offdomain_unconfirmed:${e}`) : []),
    ...[...unknownPlaceholders(next.email_subject, data.placeholders), ...unknownPlaceholders(next.email_message, data.placeholders)]
      .map(t => `placeholder_unknown:${t}`),
  ]
  const serverErrors = save.error instanceof ApiError
    ? ((save.error.body as { errors?: string[] } | null)?.errors ?? []) : []
  const shownErrors = [...new Set([...localErrors, ...serverErrors])]

  const update = (patch: { ccText?: string; copyText?: string; row?: Partial<EsignEmailSettings> }) => {
    setSaved(null)
    if (save.error) save.reset()   // a server refusal is about the values as saved; editing retires it
    setDrafts(d => ({ ...d, [tab]: { ...draft, ...patch, row: { ...draft.row, ...patch.row } } }))
  }
  const onSave = () => save.mutate(next, {
    onSuccess: row => { setDrafts(d => ({ ...d, [row.doc_type]: toDraft(row) })); setSaved(row.doc_type) },
  })

  return (
    <section style={box} aria-label="E-sign emails and copies">
      <strong style={{ fontSize: 14 }}>Emails &amp; copies</strong>
      <p style={hint}>
        Per document: who receives a copy of the signed document, what the signature email says, and who is told when it is
        signed. Left as is, a document sends exactly as it does today.
        {readOnly && <> Only an Owner or Admin can change these.</>}
      </p>

      <div role="tablist" aria-label="Document type" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {(Object.keys(DOC_TYPE_LABELS) as EsignDocType[]).map(t => (
          <button key={t} type="button" role="tab" aria-selected={t === tab} onClick={() => setTab(t)}
                  style={{ ...tabBtn, ...(t === tab ? tabOn : null) }}>
            {DOC_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        <label style={row}>
          <input type="checkbox" checked={draft.row.cc_enabled} onChange={e => update({ row: { cc_enabled: e.target.checked } })} />
          Send a copy of the signed document (DocuSign copy recipient)
        </label>
        <textarea aria-label="Copy recipients" value={draft.ccText} rows={2} style={area}
                  placeholder="one address per line, e.g. providersp@yourpharmacy.com"
                  onChange={e => update({ ccText: e.target.value })} />
        <div style={hint}>The copy is delivered by DocuSign once every signer has signed. Up to {data.limits.addresses} addresses.</div>
        {draft.row.cc_enabled && offDomain.map(e => (
          <label key={e} style={{ ...row, ...warn }}>
            <input type="checkbox" checked={draft.row.cc_offdomain_ack.includes(e)}
                   onChange={ev => update({ row: { cc_offdomain_ack: ev.target.checked
                     ? [...draft.row.cc_offdomain_ack, e] : draft.row.cc_offdomain_ack.filter(x => x !== e) } })} />
            I confirm <strong>{e}</strong> is outside {data.pharmacy_domains.length ? data.pharmacy_domains.join(', ') : "the pharmacy's domain"} and
            may receive signed patient documents.
          </label>
        ))}

        <label style={{ ...lbl, marginTop: 12 }} htmlFor={`esign-subject-${tab}`}>Email subject</label>
        <input id={`esign-subject-${tab}`} type="text" maxLength={data.limits.subject} style={inp}
               value={draft.row.email_subject ?? ''} placeholder="Signature requested: <document file name>"
               onChange={e => update({ row: { email_subject: e.target.value || null } })} />
        <label style={{ ...lbl, marginTop: 8 }} htmlFor={`esign-message-${tab}`}>Email message</label>
        <textarea id={`esign-message-${tab}`} maxLength={data.limits.message} rows={3} style={area}
                  value={draft.row.email_message ?? ''} placeholder="DocuSign's standard message"
                  onChange={e => update({ row: { email_message: e.target.value || null } })} />
        <div style={hint}>You can use {data.placeholders.join(' and ')}. Never put patient details here.</div>

        <label style={{ ...row, marginTop: 12 }}>
          <input type="checkbox" checked={draft.row.copy_enabled} onChange={e => update({ row: { copy_enabled: e.target.checked } })} />
          Email a link-only notice when it is signed
        </label>
        <textarea aria-label="Notice recipients (link only)" value={draft.copyText} rows={2} style={area}
                  placeholder="one address per line" onChange={e => update({ copyText: e.target.value })} />
        <div style={hint}>A short email with a link into OwlRx — no attachment, no patient details. Sent once per document.</div>
      </fieldset>

      {shownErrors.length > 0 && (
        <ul style={errList} role="alert">{shownErrors.map(c => <li key={c}>{describeSettingsError(c, data.limits)}</li>)}</ul>
      )}
      {save.error && serverErrors.length === 0 && <div style={errList} role="alert">{friendlyError(save.error)}</div>}

      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <button type="button" style={btn} disabled={!dirty || localErrors.length > 0 || save.isPending} onClick={onSave}>
            {save.isPending ? 'Saving…' : `Save ${DOC_TYPE_LABELS[tab]}`}
          </button>
          {saved === tab && !dirty && <span style={hint}>Saved.</span>}
        </div>
      )}
    </section>
  )
}

/** Compare what matters: blanks equal null, list order irrelevant. */
function normalise(s: EsignEmailSettings) {
  const blank = (v: string | null) => (v && v.trim() ? v.trim() : null)
  return {
    cc_enabled: s.cc_enabled, cc: [...s.cc_emails].sort(), ack: [...s.cc_offdomain_ack].sort(),
    subject: blank(s.email_subject), message: blank(s.email_message),
    copy_enabled: s.copy_enabled, copy: [...s.copy_emails].sort(),
  }
}

const box: React.CSSProperties = { border: '1px solid var(--color-border,#e2e8f0)', borderRadius: 8, padding: 14, marginBottom: 16, background: 'var(--color-surface,#fff)' }
const hint: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft,#64748b)', margin: '4px 0 0', lineHeight: 1.5 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft,#64748b)', display: 'block', marginBottom: 3 }
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4 }
const warn: React.CSSProperties = { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '5px 8px', fontSize: 12.5, marginTop: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border,#cbd5e1)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }
const area: React.CSSProperties = { ...inp, fontFamily: 'inherit', resize: 'vertical' }
const btn: React.CSSProperties = { border: '1px solid var(--color-border)', background: 'var(--color-bg)', borderRadius: 7, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const tabBtn: React.CSSProperties = { ...btn, fontWeight: 500, padding: '4px 10px', fontSize: 12.5 }
const tabOn: React.CSSProperties = { background: 'var(--color-primary-soft,#e0f2fe)', borderColor: 'var(--color-primary,#0284c7)', fontWeight: 700 }
const errList: React.CSSProperties = { color: '#991b1b', fontSize: 12.5, margin: '10px 0 0', paddingLeft: 18 }
